import { useDialog } from '@ics-portal/guardian';
import type { TraV1 } from '@ics-portal/tra';
import { useCallback, useEffect } from 'react';
import {
    type Control,
    type DefaultValues,
    type FieldValues,
    type FormState,
    useForm,
    type UseFormRegister,
    type UseFormReset,
    type UseFormReturn,
    type UseFormSetValue,
    type UseFormWatch,
} from 'react-hook-form';
import useTRA from './use-tra';

interface UseFormHandler<T extends FieldValues> {
    loading: boolean;
    close: () => void;
    register: UseFormRegister<T>;
    control: Control<T>;
    watch: UseFormWatch<T>;
    formState: FormState<T>;
    setValue: UseFormSetValue<T>;
    getValues: () => T;
    closeAfterSubmit: boolean;
    form: UseFormReturn<T>;
    reset: UseFormReset<T>;
}

interface UseFormHandlerProps<T> {
    defaultValues: DefaultValues<T>;
    onSubmit: (data: Partial<T>, rawData: Partial<T>) => Promise<void>;
    closeAfterSubmit?: boolean;
    loading?: boolean;
    disableInvalidSubmit?: boolean;
}

export function useFormHandler<T extends FieldValues>({
    defaultValues,
    onSubmit,
    closeAfterSubmit = true,
    loading,
    disableInvalidSubmit,
}: UseFormHandlerProps<T>): UseFormHandler<T> {
    const { loading: traLoading } = useTRA<TraV1>();

    const {
        handleSubmit: dialogHandleSubmit,
        close,
        setDisabled,
    } = useDialog();

    // TODO: Remove reference to traLoading
    const form = useForm<T>({
        disabled: typeof loading === 'boolean' ? loading : traLoading,
        defaultValues,
    });
    const {
        register,
        handleSubmit,
        formState,
        control,
        watch,
        setValue,
        getValues,
        reset,
    } = form;

    const submitHandler = useCallback(
        async (formValue: T): Promise<void> => {
            const keys = Object.keys(formState.dirtyFields) as (keyof T)[];
            await onSubmit(
                keys.reduce<object>(
                    (acc, k) => ({
                        ...acc,
                        [k]: formValue[k],
                    }),
                    {},
                ),
                formValue,
            );

            if (closeAfterSubmit) {
                close();
            }
        },
        [formState.dirtyFields, onSubmit, closeAfterSubmit],
    );

    useEffect(() => {
        dialogHandleSubmit(() => handleSubmit(submitHandler)());
    }, [submitHandler, submitHandler]);

    useEffect(() => {
        setDisabled(
            Object.keys(formState.dirtyFields).length === 0 ||
                (disableInvalidSubmit ? !formState.isValid : false),
        );
    }, [formState, setDisabled]);

    return {
        loading: Boolean(loading),
        register,
        control,
        watch,
        close,
        formState,
        setValue,
        getValues,
        closeAfterSubmit,
        form,
        reset,
    };
}
